import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  wizardGet,
  wizardSaveBeat,
  wizardSaveRights,
  wizardSaveLicense,
  wizardAddFile,
  wizardDeleteFile,
  wizardTransition,
} from "@/lib/bkls-wizard.functions";
import {
  WIZARD_STEPS,
  WORKFLOW_LABEL,
  WORKFLOW_TRANSITIONS,
  LICENSE_TIER_KEYS,
  WORK_KINDS,
  RIGHTS_STATUS,
  SAMPLE_STATUS,
  CLEARANCE_STATUS,
  isStepComplete,
  type WizardStepKey,
  type WorkflowStatus,
} from "@/lib/bkls-rights";

export const Route = createFileRoute("/_authenticated/admin/bkls/wizard")({
  validateSearch: (search: Record<string, unknown>) => ({
    id: typeof search.id === "string" ? search.id : undefined,
  }),
  head: () => ({ meta: [{ title: "BKLS Beat Wizard · Buddha KA" }, { name: "robots", content: "noindex" }] }),
  component: BklsWizard,
});

const input =
  "w-full rounded-lg border hairline bg-obsidian px-3 py-2 text-sm text-ivory placeholder:text-muted-foreground focus:border-gold focus:outline-none";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-gold/80";
const card = "rounded-2xl border hairline bg-charcoal p-6";
const btn =
  "rounded-full border border-gold px-4 py-2 text-xs text-gold-luminous transition-all hover:bg-gold hover:text-obsidian disabled:opacity-50";
const btnGhost = "rounded-full border hairline px-4 py-2 text-xs text-foreground/80 hover:border-gold hover:text-gold";

type BeatForm = {
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  work_kind: string;
  bpm: string;
  musical_key: string;
  genre: string;
  mood: string;
  tags: string;
  collection_slug: string;
  artwork_path: string;
  preview_path: string;
  seo_title: string;
  seo_description: string;
  sort_order: string;
};

const EMPTY: BeatForm = {
  slug: "",
  title: "",
  subtitle: "",
  description: "",
  work_kind: "beat",
  bpm: "",
  musical_key: "",
  genre: "",
  mood: "",
  tags: "",
  collection_slug: "",
  artwork_path: "",
  preview_path: "",
  seo_title: "",
  seo_description: "",
  sort_order: "0",
};

function Text({
  id,
  label,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div>
      <label className={labelCls} htmlFor={id}>
        {label}
      </label>
      <input id={id} className={input} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function BklsWizard() {
  const { id } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const load = useServerFn(wizardGet);
  const saveBeat = useServerFn(wizardSaveBeat);
  const saveRights = useServerFn(wizardSaveRights);
  const saveLicense = useServerFn(wizardSaveLicense);
  const addFile = useServerFn(wizardAddFile);
  const removeFile = useServerFn(wizardDeleteFile);
  const transition = useServerFn(wizardTransition);

  const [step, setStep] = useState<WizardStepKey>("metadata");
  const [form, setForm] = useState<BeatForm>(EMPTY);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const bundle = useQuery({
    queryKey: ["bkls", "wizard", id],
    queryFn: () => load({ data: { id: id! } }),
    enabled: Boolean(id),
  });

  useEffect(() => {
    const b: any = bundle.data?.beat;
    if (!b) return;
    setForm({
      slug: b.slug ?? "",
      title: b.title ?? "",
      subtitle: b.subtitle ?? "",
      description: b.description ?? "",
      work_kind: b.work_kind ?? "beat",
      bpm: b.bpm ? String(b.bpm) : "",
      musical_key: b.musical_key ?? "",
      genre: b.genre ?? "",
      mood: b.mood ?? "",
      tags: (b.tags ?? []).join(", "),
      collection_slug: b.collection_slug ?? "",
      artwork_path: b.artwork_path ?? "",
      preview_path: b.preview_path ?? "",
      seo_title: b.seo_title ?? "",
      seo_description: b.seo_description ?? "",
      sort_order: String(b.sort_order ?? 0),
    });
  }, [bundle.data?.beat]);

  const beat: any = bundle.data?.beat ?? null;
  const issues = bundle.data?.issues ?? [];
  const duplicates = bundle.data?.duplicates ?? [];
  const files: any[] = bundle.data?.files ?? [];
  const licenses: any[] = bundle.data?.licenses ?? [];
  const rights: any = bundle.data?.rights ?? null;
  const collections: any[] = bundle.data?.collections ?? [];
  const workflow: WorkflowStatus = (beat?.workflow_status ?? "draft") as WorkflowStatus;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ["bkls", "wizard"] });
    void queryClient.invalidateQueries({ queryKey: ["bkls", "overview"] });
  }

  async function persistBeat() {
    setBusy(true);
    setError(null);
    const result = await saveBeat({
      data: {
        ...(id ? { id } : {}),
        slug: form.slug.trim(),
        title: form.title.trim(),
        subtitle: form.subtitle,
        description: form.description,
        work_kind: form.work_kind,
        bpm: form.bpm ? Number(form.bpm) : null,
        musical_key: form.musical_key,
        genre: form.genre,
        mood: form.mood,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        collection_slug: form.collection_slug || null,
        artwork_path: form.artwork_path,
        preview_path: form.preview_path,
        seo_title: form.seo_title,
        seo_description: form.seo_description,
        sort_order: Number(form.sort_order) || 0,
      },
    });
    setBusy(false);
    if ("error" in result) {
      setError(result.error);
      return null;
    }
    setStatus(`Saved ${new Date().toLocaleTimeString()}`);
    if (!id) void navigate({ to: "/admin/bkls/wizard", search: { id: result.id } });
    refresh();
    return result.id;
  }

  const stepIndex = WIZARD_STEPS.findIndex((s) => s.key === step);

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Licensing</p>
          <h1 className="mt-2 font-serif text-3xl">{id ? "Edit beat" : "New beat"}</h1>
          {beat && (
            <p className="mt-2 text-xs text-muted-foreground">
              BKLS ID {beat.bkls_id ?? "—"} · Catalog {beat.catalog_number ?? "—"} · State{" "}
              {WORKFLOW_LABEL[workflow]} (identity fields are immutable)
            </p>
          )}
        </div>
        <Link to="/admin/bkls" className={btnGhost}>
          ← Overview
        </Link>
      </header>

      {error && (
        <p role="alert" className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </p>
      )}
      {status && <p className="text-xs text-muted-foreground">{status}</p>}

      <nav aria-label="Wizard steps">
        <ol className="flex flex-wrap gap-2">
          {WIZARD_STEPS.map((s, i) => {
            const complete = id ? isStepComplete(s.key, issues) : false;
            return (
              <li key={s.key}>
                <button
                  type="button"
                  onClick={() => setStep(s.key)}
                  aria-current={s.key === step ? "step" : undefined}
                  className={`rounded-full border px-4 py-2 text-xs ${
                    s.key === step ? "border-gold text-gold-luminous" : "hairline text-foreground/70 hover:border-gold"
                  }`}
                >
                  {i + 1}. {s.label} {complete ? "✓" : ""}
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Step 1 — identity & metadata */}
      {step === "metadata" && (
        <section className={`${card} space-y-4`} aria-labelledby="step-metadata">
          <h2 id="step-metadata" className="font-serif text-xl text-gold-luminous">
            Identity &amp; creative metadata
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Text id="w-title" label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
            <Text id="w-slug" label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} placeholder="lowercase-hyphens" />
            <div>
              <label className={labelCls} htmlFor="w-kind">
                Work kind
              </label>
              <select id="w-kind" className={input} value={form.work_kind} onChange={(e) => setForm({ ...form, work_kind: e.target.value })}>
                {WORK_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </div>
            <Text id="w-subtitle" label="Subtitle" value={form.subtitle} onChange={(v) => setForm({ ...form, subtitle: v })} />
            <Text id="w-bpm" label="BPM" value={form.bpm} onChange={(v) => setForm({ ...form, bpm: v })} />
            <Text id="w-key" label="Key" value={form.musical_key} onChange={(v) => setForm({ ...form, musical_key: v })} placeholder="F# minor" />
            <Text id="w-genre" label="Genre" value={form.genre} onChange={(v) => setForm({ ...form, genre: v })} />
            <Text id="w-mood" label="Mood" value={form.mood} onChange={(v) => setForm({ ...form, mood: v })} />
            <Text id="w-tags" label="Tags (comma separated)" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} />
            <div>
              <label className={labelCls} htmlFor="w-collection">
                Collection
              </label>
              <select
                id="w-collection"
                className={input}
                value={form.collection_slug}
                onChange={(e) => setForm({ ...form, collection_slug: e.target.value })}
              >
                <option value="">None</option>
                {collections.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls} htmlFor="w-description">
              Description
            </label>
            <textarea
              id="w-description"
              className={`${input} min-h-32`}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <button type="button" className={btn} disabled={busy} onClick={() => void persistBeat()}>
            {id ? "Save draft" : "Create beat"}
          </button>
        </section>
      )}

      {!id && step !== "metadata" && (
        <p className={`${card} text-sm text-muted-foreground`}>
          Create the beat on step 1 first — the remaining steps attach to a saved record.
        </p>
      )}

      {/* Step 2 — rights */}
      {id && step === "rights" && (
        <RightsStep
          rights={rights}
          onSave={async (payload) => {
            setError(null);
            const result = await saveRights({ data: { beat_id: id, ...payload } });
            if ("error" in result) setError(result.error);
            else {
              setStatus("Rights saved.");
              refresh();
            }
          }}
        />
      )}

      {/* Step 3/4 — artwork, preview & downloadable files */}
      {id && (step === "artwork" || step === "preview" || step === "downloads") && (
        <section className={`${card} space-y-5`} aria-labelledby="step-files">
          <h2 id="step-files" className="font-serif text-xl text-gold-luminous">
            Artwork, preview &amp; files
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Text id="w-artwork" label="Artwork storage path" value={form.artwork_path} onChange={(v) => setForm({ ...form, artwork_path: v })} />
            <Text id="w-preview" label="Preview storage path" value={form.preview_path} onChange={(v) => setForm({ ...form, preview_path: v })} />
          </div>
          <button type="button" className={btn} disabled={busy} onClick={() => void persistBeat()}>
            Save paths
          </button>

          <FileAdder
            onAdd={async (payload) => {
              setError(null);
              const result = await addFile({ data: { beat_id: id, ...payload } });
              if ("error" in result) setError(result.error);
              else {
                setStatus("File attached.");
                refresh();
              }
            }}
          />

          {files.length === 0 ? (
            <p className="text-sm text-muted-foreground">No files attached yet.</p>
          ) : (
            <ul className="space-y-2">
              {files.map((f) => (
                <li key={f.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border hairline bg-obsidian p-4">
                  <div>
                    <p className="text-sm text-ivory">
                      {f.label} <span className="text-muted-foreground">({f.kind})</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {[f.codec, f.bitrate_kbps ? `${f.bitrate_kbps} kbps` : null, f.sample_rate ? `${f.sample_rate} Hz` : null, f.checksum ? `checksum ${String(f.checksum).slice(0, 12)}…` : null]
                        .filter(Boolean)
                        .join(" · ") || f.storage_path}
                    </p>
                  </div>
                  <button
                    type="button"
                    className={btnGhost}
                    onClick={async () => {
                      await removeFile({ data: { id: f.id } });
                      refresh();
                    }}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">
            Values are recorded as declared by the steward; the platform validates extensions, sizes and duplicate
            checksums but does not decode audio server-side.
          </p>
        </section>
      )}

      {/* Step 5 — licences */}
      {id && step === "licenses" && (
        <section className={`${card} space-y-4`} aria-labelledby="step-licenses">
          <h2 id="step-licenses" className="font-serif text-xl text-gold-luminous">
            Licence tiers
          </h2>
          {LICENSE_TIER_KEYS.map((tier) => {
            const existing = licenses.find((l) => l.tier_key === tier);
            return (
              <LicenseRow
                key={tier}
                tierKey={tier}
                existing={existing}
                onSave={async (payload) => {
                  setError(null);
                  const result = await saveLicense({ data: { beat_id: id, tier_key: tier, ...payload } });
                  if ("error" in result) setError(result.error);
                  else {
                    setStatus(`${tier} licence saved.`);
                    refresh();
                  }
                }}
              />
            );
          })}
          <p className="text-xs text-muted-foreground">
            Custom and exclusive tiers stay quote-only — no fixed price is ever published for them.
          </p>
        </section>
      )}

      {/* Step 6 — SEO */}
      {id && step === "seo" && (
        <section className={`${card} space-y-4`} aria-labelledby="step-seo">
          <h2 id="step-seo" className="font-serif text-xl text-gold-luminous">
            SEO &amp; discovery
          </h2>
          <Text id="w-seo-title" label="SEO title" value={form.seo_title} onChange={(v) => setForm({ ...form, seo_title: v })} />
          <div>
            <label className={labelCls} htmlFor="w-seo-desc">
              SEO description
            </label>
            <textarea
              id="w-seo-desc"
              className={`${input} min-h-24`}
              value={form.seo_description}
              onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Canonical: /beats/{form.slug || "…"} · Structured data: MusicRecording + BreadcrumbList
          </p>
          <button type="button" className={btn} disabled={busy} onClick={() => void persistBeat()}>
            Save
          </button>
        </section>
      )}

      {/* Step 7 — review */}
      {id && step === "review" && (
        <section className={`${card} space-y-4`} aria-labelledby="step-review">
          <h2 id="step-review" className="font-serif text-xl text-gold-luminous">
            Quality &amp; legal review
          </h2>
          {issues.length === 0 ? (
            <p className="text-sm text-muted-foreground">No blocking issues. This beat can be scheduled or published.</p>
          ) : (
            <ul className="space-y-2" aria-label="Blocking issues">
              {issues.map((issue, i) => (
                <li key={`${issue.field}-${i}`} className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
                  {issue.message}{" "}
                  <button type="button" className="underline" onClick={() => setStep(issue.step)}>
                    Go to {issue.step}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {duplicates.length > 0 && (
            <ul className="space-y-2" aria-label="Warnings">
              {duplicates.map((d, i) => (
                <li key={`${d.field}-${i}`} className="rounded-xl border hairline bg-obsidian p-3 text-sm text-muted-foreground">
                  {d.message} (matches {d.matchSlug})
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Step 8 — workflow */}
      {id && step === "review" && (
        <section className={`${card} space-y-4`} aria-labelledby="step-workflow">
          <h2 id="step-workflow" className="font-serif text-xl text-gold-luminous">
            Workflow &amp; publish
          </h2>
          <p className="text-sm text-muted-foreground">Current state: {WORKFLOW_LABEL[workflow]}</p>
          <div className="flex flex-wrap gap-2">
            {(WORKFLOW_TRANSITIONS[workflow] ?? []).map((next) => (
              <button
                key={next}
                type="button"
                className={btn}
                onClick={async () => {
                  setError(null);
                  const scheduledAt =
                    next === "scheduled" ? new Date(Date.now() + 24 * 3600 * 1000).toISOString() : null;
                  const result = await transition({ data: { id, to: next, scheduled_at: scheduledAt } });
                  if ("error" in result) setError(result.error);
                  else {
                    setStatus(`Moved to ${WORKFLOW_LABEL[next]}.`);
                    refresh();
                  }
                }}
              >
                Move to {WORKFLOW_LABEL[next]}
              </button>
            ))}
            {(WORKFLOW_TRANSITIONS[workflow] ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground">This state is terminal.</p>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Only valid transitions are offered, and the server rejects anything else — a draft can never jump
            straight to published.
          </p>
        </section>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          className={btnGhost}
          disabled={stepIndex === 0}
          onClick={() => setStep(WIZARD_STEPS[Math.max(0, stepIndex - 1)].key)}
        >
          ← Previous
        </button>
        <button
          type="button"
          className={btnGhost}
          disabled={stepIndex === WIZARD_STEPS.length - 1}
          onClick={() => setStep(WIZARD_STEPS[Math.min(WIZARD_STEPS.length - 1, stepIndex + 1)].key)}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

function RightsStep({ rights, onSave }: { rights: any; onSave: (payload: any) => Promise<void> }) {
  const [state, setState] = useState({
    rights_status: rights?.rights_status ?? "unverified",
    sample_status: rights?.sample_status ?? "original",
    clearance_status: rights?.clearance_status ?? "pending",
    copyright_owner: rights?.copyright_owner ?? "",
    master_owner: rights?.master_owner ?? "",
    composition_owner: rights?.composition_owner ?? "",
    publisher: rights?.publisher ?? "",
    producer: rights?.producer ?? "",
    writers: (rights?.writers ?? []).join(", "),
    collaborators: (rights?.collaborators ?? []).join(", "),
    pro: rights?.pro ?? "",
    iswc: rights?.iswc ?? "",
    isrc: rights?.isrc ?? "",
    copyright_registration_number: rights?.copyright_registration_number ?? "",
    notes: rights?.notes ?? "",
  });

  return (
    <section className={`${card} space-y-4`} aria-labelledby="step-rights">
      <h2 id="step-rights" className="font-serif text-xl text-gold-luminous">
        Rights &amp; clearance
      </h2>
      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className={labelCls} htmlFor="r-status">
            Rights status
          </label>
          <select id="r-status" className={input} value={state.rights_status} onChange={(e) => setState({ ...state, rights_status: e.target.value })}>
            {RIGHTS_STATUS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="r-sample">
            Sample status
          </label>
          <select id="r-sample" className={input} value={state.sample_status} onChange={(e) => setState({ ...state, sample_status: e.target.value })}>
            {SAMPLE_STATUS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="r-clearance">
            Clearance
          </label>
          <select id="r-clearance" className={input} value={state.clearance_status} onChange={(e) => setState({ ...state, clearance_status: e.target.value })}>
            {CLEARANCE_STATUS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <Text id="r-copyright" label="Copyright owner" value={state.copyright_owner} onChange={(v) => setState({ ...state, copyright_owner: v })} />
        <Text id="r-master" label="Master owner" value={state.master_owner} onChange={(v) => setState({ ...state, master_owner: v })} />
        <Text id="r-composition" label="Composition owner" value={state.composition_owner} onChange={(v) => setState({ ...state, composition_owner: v })} />
        <Text id="r-publisher" label="Publisher" value={state.publisher} onChange={(v) => setState({ ...state, publisher: v })} />
        <Text id="r-producer" label="Producer" value={state.producer} onChange={(v) => setState({ ...state, producer: v })} />
        <Text id="r-pro" label="PRO" value={state.pro} onChange={(v) => setState({ ...state, pro: v })} />
        <Text id="r-writers" label="Writers (comma separated)" value={state.writers} onChange={(v) => setState({ ...state, writers: v })} />
        <Text id="r-collabs" label="Collaborators" value={state.collaborators} onChange={(v) => setState({ ...state, collaborators: v })} />
        <Text id="r-iswc" label="ISWC" value={state.iswc} onChange={(v) => setState({ ...state, iswc: v })} />
        <Text id="r-isrc" label="ISRC" value={state.isrc} onChange={(v) => setState({ ...state, isrc: v })} />
        <Text
          id="r-reg"
          label="Copyright registration"
          value={state.copyright_registration_number}
          onChange={(v) => setState({ ...state, copyright_registration_number: v })}
        />
      </div>
      <div>
        <label className={labelCls} htmlFor="r-notes">
          Notes
        </label>
        <textarea id="r-notes" className={`${input} min-h-24`} value={state.notes} onChange={(e) => setState({ ...state, notes: e.target.value })} />
      </div>
      <button
        type="button"
        className={btn}
        onClick={() =>
          void onSave({
            ...state,
            writers: state.writers.split(",").map((w: string) => w.trim()).filter(Boolean),
            collaborators: state.collaborators.split(",").map((w: string) => w.trim()).filter(Boolean),
          })
        }
      >
        Save rights
      </button>
    </section>
  );
}

function FileAdder({ onAdd }: { onAdd: (payload: any) => Promise<void> }) {
  const [state, setState] = useState({
    kind: "preview",
    label: "",
    storage_path: "",
    format: "",
    size_bytes: "",
    checksum: "",
    codec: "",
    bitrate_kbps: "",
    sample_rate: "",
    channels: "",
    duration_seconds: "",
  });

  return (
    <div className="rounded-xl border hairline bg-obsidian p-4">
      <h3 className="text-sm text-ivory">Attach a file</h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <div>
          <label className={labelCls} htmlFor="f-kind">
            Kind
          </label>
          <select id="f-kind" className={input} value={state.kind} onChange={(e) => setState({ ...state, kind: e.target.value })}>
            {["preview", "mp3", "wav", "stems", "artwork", "other"].map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>
        <Text id="f-label" label="Label" value={state.label} onChange={(v) => setState({ ...state, label: v })} />
        <Text id="f-path" label="Storage path" value={state.storage_path} onChange={(v) => setState({ ...state, storage_path: v })} />
        <Text id="f-format" label="Format" value={state.format} onChange={(v) => setState({ ...state, format: v })} />
        <Text id="f-size" label="Size (bytes)" value={state.size_bytes} onChange={(v) => setState({ ...state, size_bytes: v })} />
        <Text id="f-checksum" label="Checksum" value={state.checksum} onChange={(v) => setState({ ...state, checksum: v })} />
        <Text id="f-codec" label="Codec" value={state.codec} onChange={(v) => setState({ ...state, codec: v })} />
        <Text id="f-bitrate" label="Bitrate (kbps)" value={state.bitrate_kbps} onChange={(v) => setState({ ...state, bitrate_kbps: v })} />
        <Text id="f-samplerate" label="Sample rate" value={state.sample_rate} onChange={(v) => setState({ ...state, sample_rate: v })} />
        <Text id="f-channels" label="Channels" value={state.channels} onChange={(v) => setState({ ...state, channels: v })} />
        <Text id="f-duration" label="Duration (s)" value={state.duration_seconds} onChange={(v) => setState({ ...state, duration_seconds: v })} />
      </div>
      <button
        type="button"
        className={`${btn} mt-4`}
        onClick={() =>
          void onAdd({
            kind: state.kind,
            label: state.label,
            storage_path: state.storage_path,
            format: state.format,
            size_bytes: state.size_bytes ? Number(state.size_bytes) : undefined,
            checksum: state.checksum,
            codec: state.codec,
            bitrate_kbps: state.bitrate_kbps ? Number(state.bitrate_kbps) : null,
            sample_rate: state.sample_rate ? Number(state.sample_rate) : null,
            channels: state.channels ? Number(state.channels) : null,
            duration_seconds: state.duration_seconds ? Number(state.duration_seconds) : null,
          })
        }
      >
        Attach file
      </button>
    </div>
  );
}

function LicenseRow({
  tierKey,
  existing,
  onSave,
}: {
  tierKey: string;
  existing: any;
  onSave: (payload: any) => Promise<void>;
}) {
  const quoteOnly = tierKey === "custom" || tierKey === "exclusive";
  const [state, setState] = useState({
    enabled: existing?.enabled ?? false,
    price: existing?.price_cents ? String(existing.price_cents / 100) : "",
    download_package: (existing?.download_package ?? []).join(", "),
    stream_limit: existing?.stream_limit ?? "",
    video_limit: existing?.video_limit ?? "",
    performance_rights: existing?.performance_rights ?? "",
    monetization: existing?.monetization ?? "",
    sync_allowed: existing?.sync_allowed ?? false,
    credit_requirement: existing?.credit_requirement ?? "",
    territory: existing?.territory ?? "Worldwide",
    duration: existing?.duration ?? "Perpetual",
  });

  return (
    <div className="rounded-xl border hairline bg-obsidian p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-serif text-lg capitalize text-ivory">{tierKey}</h3>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={state.enabled} onChange={(e) => setState({ ...state, enabled: e.target.checked })} />
          Enabled
        </label>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        {!quoteOnly && <Text id={`l-${tierKey}-price`} label="Price (major units)" value={state.price} onChange={(v) => setState({ ...state, price: v })} />}
        <Text id={`l-${tierKey}-package`} label="Download package" value={state.download_package} onChange={(v) => setState({ ...state, download_package: v })} />
        <Text id={`l-${tierKey}-stream`} label="Stream limit" value={state.stream_limit} onChange={(v) => setState({ ...state, stream_limit: v })} />
        <Text id={`l-${tierKey}-video`} label="Video limit" value={state.video_limit} onChange={(v) => setState({ ...state, video_limit: v })} />
        <Text id={`l-${tierKey}-perf`} label="Performance rights" value={state.performance_rights} onChange={(v) => setState({ ...state, performance_rights: v })} />
        <Text id={`l-${tierKey}-monet`} label="Monetization" value={state.monetization} onChange={(v) => setState({ ...state, monetization: v })} />
        <Text id={`l-${tierKey}-credit`} label="Credit requirement" value={state.credit_requirement} onChange={(v) => setState({ ...state, credit_requirement: v })} />
        <Text id={`l-${tierKey}-territory`} label="Territory" value={state.territory} onChange={(v) => setState({ ...state, territory: v })} />
        <Text id={`l-${tierKey}-duration`} label="Duration" value={state.duration} onChange={(v) => setState({ ...state, duration: v })} />
      </div>
      <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
        <input type="checkbox" checked={state.sync_allowed} onChange={(e) => setState({ ...state, sync_allowed: e.target.checked })} />
        Sync licensing allowed
      </label>
      <button
        type="button"
        className={`${btn} mt-4`}
        onClick={() =>
          void onSave({
            enabled: state.enabled,
            price_cents: quoteOnly || !state.price ? null : Math.round(Number(state.price) * 100),
            download_package: state.download_package.split(",").map((s: string) => s.trim()).filter(Boolean),
            stream_limit: state.stream_limit,
            video_limit: state.video_limit,
            performance_rights: state.performance_rights,
            monetization: state.monetization,
            sync_allowed: state.sync_allowed,
            credit_requirement: state.credit_requirement,
            territory: state.territory,
            duration: state.duration,
          })
        }
      >
        Save {tierKey}
      </button>
      {quoteOnly && <p className="mt-2 text-xs text-muted-foreground">Quote-only tier — no fixed price is published.</p>}
    </div>
  );
}
